package org.dronda.app.web.ui.eventadmin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import org.dronda.service.wishlist.core.domain.EventMode
import org.dronda.service.wishlist.core.domain.SantaExclusionDirection
import org.dronda.app.web.Loader
import org.dronda.app.web.ui.components.AppColors
import org.dronda.app.web.ui.components.Card
import org.dronda.app.web.ui.components.Dialog
import org.dronda.app.web.ui.components.ErrorText
import org.dronda.app.web.ui.components.ExclusionLabelText
import org.dronda.app.web.ui.components.ListRow
import org.dronda.app.web.ui.components.PageColumn
import org.dronda.app.web.ui.components.SelectField
import org.dronda.app.web.ui.components.StyledButton
import org.dronda.app.web.ui.components.TextField
import org.dronda.app.web.ui.eventadmin.EventAdminViewModel.Event
import org.dronda.app.web.ui.eventadmin.EventAdminViewModel.UiState
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun EventAdminScreen(
    viewModel: EventAdminViewModel,
    onBackClick: () -> Unit,
) {
    val state by viewModel.uiState.collectAsState()
    val take = viewModel::take

    PageColumn {
        Span(Modifier.cursor(Cursor.Pointer).onClick { onBackClick() }.toAttrs()) { Text("◀ Back to event") }

        when {
            state.loading -> Loader()
            state.loadError != null -> ErrorText(state.loadError!!)
            else -> {
                H1(Modifier.fontWeight(FontWeight.Light).toAttrs()) { Text("⚙ Admin · ${state.title}") }
                SettingsSection(state, take)
                PeopleSection(state, take)
                if (state.isSecretSanta) {
                    ExclusionsSection(state, take)
                    SendSection(state, take)
                }
            }
        }
    }

    if (state.confirmingSend) {
        Dialog(title = "Send Secret Santa emails?", onDismiss = { take(Event.CancelSend) }) {
            P(Modifier.color(AppColors.muted).toAttrs()) {
                Text("This uses one of your sends and can't be undone.")
            }
            Row(Modifier.fillMaxWidth().gap(0.5.cssRem), horizontalArrangement = Arrangement.End) {
                StyledButton("Cancel", onClick = { take(Event.CancelSend) })
                StyledButton("Send", onClick = { take(Event.ConfirmSend) }, primary = true)
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String, trailing: String? = null) {
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        H3(Modifier.fontWeight(FontWeight.Normal).toAttrs()) { Text(text) }
        trailing?.let { Span(Modifier.color(AppColors.muted).toAttrs()) { Text(it) } }
    }
}

@Composable
private fun SettingsSection(state: UiState, take: (Event) -> Unit) {
    Card {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(0.75.cssRem)) {
            SectionTitle("Settings")
            TextField(state.name, { take(Event.NameChanged(it)) }, "Name", "admin-name-input")
            TextField(state.date, { take(Event.DateChanged(it)) }, "Date", "admin-date-input", InputType.Date)
            if (state.canChangeMode) {
                Row(Modifier.gap(1.cssRem).flexWrap(FlexWrap.Wrap)) {
                    RadioOption("event-mode", "🎅 Secret Santa", state.mode == EventMode.SECRET_SANTA) {
                        take(Event.ModeChanged(EventMode.SECRET_SANTA))
                    }
                    RadioOption("event-mode", "🎁 Wishlists", state.mode == EventMode.STANDARD) {
                        take(Event.ModeChanged(EventMode.STANDARD))
                    }
                }
            }
            if (state.mode == EventMode.SECRET_SANTA) {
                TextField(state.budget, { take(Event.BudgetChanged(it)) }, "Budget (optional)", "admin-budget-input")
            }
            Row(
                Modifier.fillMaxWidth().gap(0.5.cssRem),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                state.settingsMessage?.let { Span(Modifier.color(AppColors.muted).toAttrs()) { Text(it) } }
                StyledButton("Save", onClick = { take(Event.SaveSettings) }, primary = true, disabled = state.busy)
            }
        }
    }
}

@Composable
private fun PeopleSection(state: UiState, take: (Event) -> Unit) {
    Card {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
            SectionTitle(state.peopleTitle)
            state.people.forEach { person -> ListRow(label = person.label, detail = person.status) }
            if (state.canAddPeople) {
                Row(
                    Modifier.fillMaxWidth().gap(0.5.cssRem),
                    verticalAlignment = Alignment.Bottom,
                ) {
                    TextField(
                        value = state.emailDraft,
                        onValueChange = { take(Event.EmailDraftChanged(it)) },
                        label = "Add a person by email",
                        labelId = "admin-add-person-input",
                        inputType = InputType.Email,
                        error = state.addPersonError,
                        onEnter = { take(Event.AddPerson) },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    StyledButton("+ Add", onClick = { take(Event.AddPerson) }, disabled = state.busy)
                }
            }
        }
    }
}

@Composable
private fun ExclusionsSection(state: UiState, take: (Event) -> Unit) {
    Card {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
            SectionTitle("Exclusions")
            P(Modifier.color(AppColors.muted).fontSize(0.9.cssRem).toAttrs()) {
                Text("Changes apply to the next draw. Saving changes after names are drawn lets you reshuffle.")
            }
            state.exclusions.forEach { row ->
                ListRow(onRemove = { take(Event.RemoveExclusion(row.exclusion)) }) { ExclusionLabelText(row.label) }
            }
            Row(
                Modifier.fillMaxWidth().gap(0.5.cssRem).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SelectField(state.choices, state.exclusionA, "Choose someone", { take(Event.ExclusionAChanged(it)) })
                SelectField(
                    state.directionChoices,
                    state.exclusionDirection.name,
                    "Direction",
                    { selected ->
                        selected?.let { take(Event.ExclusionDirectionChanged(SantaExclusionDirection.valueOf(it))) }
                    },
                )
                SelectField(state.choices, state.exclusionB, "Choose someone", { take(Event.ExclusionBChanged(it)) })
                StyledButton("+ Add pair", onClick = { take(Event.AddExclusion) })
            }
            Row(
                Modifier.fillMaxWidth().gap(0.5.cssRem),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                state.exclusionsMessage?.let { Span(Modifier.color(AppColors.muted).toAttrs()) { Text(it) } }
                StyledButton(
                    "Save exclusions",
                    onClick = { take(Event.SaveExclusions) },
                    primary = true,
                    disabled = state.busy || !state.exclusionsDirty,
                )
            }
        }
    }
}

@Composable
private fun SendSection(state: UiState, take: (Event) -> Unit) {
    val panel = state.sendPanel ?: return
    Card {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(0.75.cssRem)) {
            SectionTitle("Secret Santa emails", trailing = panel.sendsLabel)
            P { Text(panel.summary) }
            panel.options.forEach { option ->
                Column {
                    RadioOption("send-kind", option.title, state.selectedKind == option.kind) {
                        take(Event.SendKindSelected(option.kind))
                    }
                    P(Modifier.color(AppColors.muted).fontSize(0.85.cssRem).toAttrs()) { Text(option.description) }
                }
            }
            state.sendMessage?.let { P { Text(it) } }
            panel.buttonLabel?.let { label ->
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    if (state.busy) {
                        Loader()
                    } else {
                        StyledButton(label, onClick = { take(Event.RequestSend) }, primary = true)
                    }
                }
            }
        }
    }
}

@Composable
private fun RadioOption(group: String, label: String, checked: Boolean, onSelect: () -> Unit) {
    Label(attrs = { style { property("cursor", "pointer") } }) {
        Input(InputType.Radio) {
            name(group)
            checked(checked)
            onChange { onSelect() }
        }
        Text(" $label")
    }
}
